'use client';

/**
 * Indeterminate top-of-page loading indicator.
 *
 * Replaces the old `react-top-loading-bar` (a flat solid bar with a hard drop
 * shadow — see the "ugly shadow" it used to cast under the header). This is a
 * pure-CSS flowing gradient: two soft, brand-tinted blobs glide across a
 * hairline track pinned to the very top of the viewport, so a route transition
 * reads as gentle motion rather than a heavy block sliding in.
 *
 * Colour comes entirely from the tenant brand (`--primary`, i.e.
 * `--theme-color`), so it stays on-brand for every tenant and in both light and
 * dark themes. No JS timers, no external dependency: it animates while mounted
 * (during a Suspense/`loading.tsx` boundary) and disappears the instant the
 * route is ready, which is exactly the lifecycle Next.js gives it.
 */
export function TopLoadingBarIndicator() {
  return (
    <div
      role="progressbar"
      aria-busy="true"
      aria-label="Loading"
      className="zameel-toploader"
    >
      <span className="zameel-toploader__blob zameel-toploader__blob--lead" />
      <span className="zameel-toploader__blob zameel-toploader__blob--glow" />

      <style>{styles}</style>
    </div>
  );
}

const styles = `
.zameel-toploader {
  position: fixed;
  inset-block-start: 0;
  inset-inline: 0;
  z-index: 100;
  height: 3px;
  overflow: hidden;
  pointer-events: none;
  /* Faint brand-tinted track — replaces the old opaque bar + drop shadow. */
  background: color-mix(in oklab, var(--primary) 10%, transparent);
}

.zameel-toploader__blob {
  position: absolute;
  /* Bleed above/below the 3px track so the blur glows instead of clipping. */
  inset-block: -4px;
  border-radius: 9999px;
  will-change: transform;
}

/* The main sweep: an iridescent brand gradient with a bright core, giving the
   "bubble" shimmer as it flows across. */
.zameel-toploader__blob--lead {
  width: 46%;
  filter: blur(1.5px);
  background: linear-gradient(
    90deg,
    transparent 0%,
    color-mix(in oklab, var(--primary) 55%, transparent) 28%,
    var(--primary) 46%,
    color-mix(in oklab, var(--primary) 65%, white) 54%,
    color-mix(in oklab, var(--primary) 55%, transparent) 72%,
    transparent 100%
  );
  animation: zameel-toploader-sweep 2s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}

/* A smaller, softer trailing highlight offset in time — the second "bubble"
   that keeps the motion feeling organic rather than a single rigid segment.
   The delay is ~half the cycle so one blob is always mid-track: the sweep
   reads as a continuous flow instead of a single dart that resets. */
.zameel-toploader__blob--glow {
  width: 26%;
  opacity: 0.65;
  filter: blur(3px);
  background: linear-gradient(
    90deg,
    transparent 0%,
    color-mix(in oklab, var(--primary) 45%, white) 50%,
    transparent 100%
  );
  animation: zameel-toploader-sweep 2s cubic-bezier(0.65, 0, 0.35, 1) 1s
    infinite;
}

@keyframes zameel-toploader-sweep {
  0% {
    transform: translateX(-130%);
  }
  100% {
    transform: translateX(340%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .zameel-toploader__blob {
    animation-duration: 2.6s;
    filter: none;
  }
}
`;
